import { axiosInstance } from '@/Api/axiosV2Config';
import type {
  WarrantyPreset,
  WarrantyPresetDetail,
  CreateWarrantyPresetDto,
  UpdateWarrantyPresetDto,
  UpdatePresetPartsDto,
} from '../types/warranty-preset.types';

/**
 * API client pour la gestion des presets de garantie
 * Utilise les endpoints /warranty-presets
 */
export const warrantyPresetApi = {
  // ==================== LECTURE ====================

  /**
   * Récupérer les presets selon critères (vehicleType)
   */
  getPresetsByCriteria: async (
    vehicleType?: number,
    lang = 'fr',
  ): Promise<WarrantyPreset[]> => {
    const params: Record<string, string | number> = { lang };
    if (vehicleType !== undefined) params.vehicleType = vehicleType;

    const { data } = await axiosInstance.get<WarrantyPreset[]>('/warranty-presets', {
      params,
    });
    return data;
  },

  /**
   * Récupérer tous les presets (admin)
   */
  getAllPresets: async (): Promise<WarrantyPreset[]> => {
    const { data } = await axiosInstance.get<WarrantyPreset[]>('/warranty-presets/admin/all');
    return data;
  },

  /**
   * Récupérer un preset par ID avec détails complets (incluant les pièces)
   */
  getPresetById: async (id: number, lang = 'fr'): Promise<WarrantyPresetDetail> => {
    const { data } = await axiosInstance.get<WarrantyPresetDetail>(`/warranty-presets/${id}`, {
      params: { lang },
    });
    return data;
  },

  // ==================== CRUD (Internal users only) ====================

  /**
   * Créer un nouveau preset
   */
  createPreset: async (preset: CreateWarrantyPresetDto): Promise<WarrantyPreset> => {
    const { data } = await axiosInstance.post<WarrantyPreset>('/warranty-presets', preset);
    return data;
  },

  /**
   * Mettre à jour un preset (informations générales)
   */
  updatePreset: async (
    id: number,
    preset: UpdateWarrantyPresetDto,
  ): Promise<WarrantyPreset> => {
    const { data } = await axiosInstance.patch<WarrantyPreset>(
      `/warranty-presets/${id}`,
      preset,
    );
    return data;
  },

  /**
   * Mettre à jour uniquement les pièces d'un preset
   */
  updatePresetParts: async (id: number, parts: UpdatePresetPartsDto): Promise<WarrantyPreset> => {
    const { data } = await axiosInstance.patch<WarrantyPreset>(
      `/warranty-presets/${id}/parts`,
      parts,
    );
    return data;
  },

  /**
   * Supprimer un preset
   */
  deletePreset: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/warranty-presets/${id}`);
  },
};
